import React from 'react';
import { Bug, Lightbulb, Heart } from 'lucide-react';
import { FeedbackKind } from '../../client/feedbackClient';

/**
 * Step one: three big tappable choices. Big because the whole point is that
 * telling us something takes one thumb and no thinking — a dropdown here would
 * be a form, and nobody fills in a form to say "this was nice".
 *
 * Cobalt is the user's action colour, so the chosen-state wash is cobalt, not
 * violet: picking a kind is the person's doing, not Robbie's.
 */

interface Choice {
  kind: FeedbackKind;
  label: string;
  hint: string;
  icon: React.ReactNode;
}

export const FEEDBACK_CHOICES: Choice[] = [
  { kind: 'PROBLEM', label: "Something's wrong", hint: 'A bug, or something that got in your way', icon: <Bug /> },
  { kind: 'IDEA', label: 'An idea', hint: 'Something you wish this did', icon: <Lightbulb /> },
  { kind: 'PRAISE', label: 'Something I liked', hint: 'Worth keeping — tell me which bit', icon: <Heart /> },
];

export interface FeedbackKindChoicesProps {
  onChoose: (kind: FeedbackKind) => void;
}

export const FeedbackKindChoices: React.FC<FeedbackKindChoicesProps> = ({ onChoose }) => (
  <div className="flex flex-col gap-s3">
    {FEEDBACK_CHOICES.map((choice, index) => (
      <button
        key={choice.kind}
        type="button"
        autoFocus={index === 0}
        onClick={() => onChoose(choice.kind)}
        className="flex items-center gap-s4 rounded-card border border-line bg-surface px-s5 py-s4 text-left transition-colors duration-100 hover:border-action-border hover:bg-action-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-action motion-reduce:transition-none"
      >
        <span className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-control border border-line bg-canvas text-ink-secondary [&>svg]:h-[17px] [&>svg]:w-[17px]">
          {choice.icon}
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="text-row-title text-ink">{choice.label}</span>
          <span className="text-meta text-ink-muted">{choice.hint}</span>
        </span>
      </button>
    ))}
  </div>
);

export default FeedbackKindChoices;
